import React from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { X, Settings, AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const RecoveryOptionsDialog = ({ isOpen, onClose, onSuccess, hasExistingAdmins = false, integrationStatus = null }) => {
  const { keycloak } = useKeycloak();
  const { t } = useTranslation();

  const userEmail = keycloak.tokenParsed?.email;
  const userDomain = userEmail?.split('@')[1];

  const handleSetupIntegration = () => {
    console.log('🚀 Recovery Dialog: Redirecting to integrations with fresh start');
    console.log('🚀 Recovery Dialog: Current URL:', window.location.href);
    console.log('🚀 Recovery Dialog: Target URL:', '/integrations?start=fresh');
    onClose();
    // Add a query parameter to force starting from step 1
    window.location.href = '/integrations?start=fresh';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-start pt-20 p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full p-8 shadow-2xl border-2 border-blue-200 animate-in slide-in-from-top-4 duration-300">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-full mr-4">
              <AlertCircle className="text-blue-600" size={28} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{t('recovery.title')}</h2>
              <p className="text-gray-600 text-sm mt-1">{t('recovery.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
          >
            <X size={24} />
          </button>
        </div>

        <div className="mb-8">
          <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-xl p-6 mb-6">
            <div className="flex items-start">
              <CheckCircle className="text-green-600 mr-4 mt-1 flex-shrink-0" size={24} />
              <div>
                <p className="text-gray-800 font-semibold text-lg">
                  {t('recovery.loggedInMessage')}
                </p>
                <p className="text-gray-700 mt-2 leading-relaxed">
                  {hasExistingAdmins 
                    ? t('recovery.adminExistsMessage')
                    : t('integrations.warning.noAdminMessage')
                  }
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-gray-600 text-sm font-medium">{t('recovery.yourEmail')}</p>
                <p className="text-gray-800 font-mono text-sm">{userEmail}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">{t('recovery.yourDomain')}</p>
                <p className="text-gray-800 font-mono text-sm">{userDomain}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-8">
            {t('recovery.getStarted')}
          </h3>
          
          <button
            onClick={handleSetupIntegration}
            className="w-full flex items-center justify-center px-8 py-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <div className="bg-white bg-opacity-20 p-3 rounded-lg mr-4">
              <Settings size={28} />
            </div>
            <div className="text-left flex-1">
              <div className="font-semibold text-xl">{t('recovery.setupIntegration')}</div>
              <div className="text-blue-100 text-sm mt-1">{t('recovery.setupIntegrationDesc')}</div>
            </div>
            <div className="text-blue-200 text-xl">→</div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecoveryOptionsDialog;